import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ResponsiveOperationalTable from "../../components/dashboard/ResponsiveOperationalTable";
import ContextualHelp from "../../components/dashboard/ContextualHelp";
import DashboardLoadingState from "../../components/dashboard/DashboardLoadingState";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { appointmentsApi } from "../../api/appointments";
import { productsApi } from "../../api/products";
import ModuleDisabledState from "../../components/dashboard/ModuleDisabledState";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import { dashboardProjectItemsPath } from "../../utils/dashboardNavigation";

const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };
const TIMEZONE = "America/Argentina/Mendoza";
const ACTIVE_STATUSES = ["pending", "confirmed"];

const DAYS = [
  { weekday: 1, label: "Lunes" },
  { weekday: 2, label: "Martes" },
  { weekday: 3, label: "Miércoles" },
  { weekday: 4, label: "Jueves" },
  { weekday: 5, label: "Viernes" },
  { weekday: 6, label: "Sábado" },
  { weekday: 0, label: "Domingo" },
];

const emptyAppointment = {
  fullName: "",
  phone: "",
  email: "",
  startAt: "",
  notes: "",
  status: "confirmed",
  paymentStatus: "not_required",
};

const emptyBlock = {
  scope: "service",
  startAt: "",
  endAt: "",
  reason: "",
};

function normalizeType(item = {}) {
  const value = String(item.itemType || item.type || item.tipo || "")
    .trim()
    .toLowerCase();
  return ["service", "servicio"].includes(value) ? "service" : "product";
}

function itemId(item = {}) {
  return item.id || item._id || "";
}

function itemName(item = {}) {
  return item.name || item.nombre || "Servicio";
}

function requiresBooking(item = {}) {
  return Boolean(item.requiresBooking ?? item.requiereReserva);
}

function durationLabel(item = {}) {
  const minutes = Number(item.durationMinutes ?? item.duracionMinutos ?? 0);
  if (!minutes) return "Sin duración";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

function localInputToIso(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
}

function isoToLocalInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function statusLabel(status) {
  return {
    pending: "Pendiente",
    confirmed: "Confirmado",
    cancelled: "Cancelado",
    completed: "Completado",
    no_show: "No asistió",
  }[status] || status;
}

function paymentLabel(status) {
  return {
    not_required: "No requerido",
    pending: "Pendiente",
    in_process: "En proceso",
    approved: "Pagado",
    rejected: "Rechazado",
    cancelled: "Cancelado",
    refunded: "Devuelto",
  }[status] || status;
}

function defaultSchedule() {
  return DAYS.map((day) => ({
    ...day,
    enabled: day.weekday >= 1 && day.weekday <= 5,
    startTime: "09:00",
    endTime: "18:00",
  }));
}

function rulesToSchedule(rules = []) {
  const firstByDay = new Map();
  for (const rule of rules) {
    if (rule?.active === false || firstByDay.has(Number(rule.weekday))) continue;
    firstByDay.set(Number(rule.weekday), rule);
  }
  return DAYS.map((day) => {
    const rule = firstByDay.get(day.weekday);
    return {
      ...day,
      enabled: Boolean(rule),
      startTime: rule?.startTime || "09:00",
      endTime: rule?.endTime || "18:00",
    };
  });
}

function normalizeSchedule(schedule = []) {
  return DAYS.map((day) => {
    const current = schedule.find((item) => Number(item?.weekday) === day.weekday);
    return {
      weekday: day.weekday,
      enabled: Boolean(current?.enabled),
      startTime: current?.startTime || "09:00",
      endTime: current?.endTime || "18:00",
    };
  });
}

function schedulesMatch(left, right) {
  if (!left || !right) return false;
  return JSON.stringify(normalizeSchedule(left)) === JSON.stringify(normalizeSchedule(right));
}

const SCHEDULE_TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

function validateScheduleDay(day) {
  if (!day?.enabled) {
    return { valid: true, startTimeError: "", endTimeError: "" };
  }

  const startTime = String(day.startTime || "").trim();
  const endTime = String(day.endTime || "").trim();
  let startTimeError = "";
  let endTimeError = "";

  if (!startTime) {
    startTimeError = "Completá el horario de este día.";
  } else if (!SCHEDULE_TIME_PATTERN.test(startTime)) {
    startTimeError = "Ingresá un horario válido.";
  }

  if (!endTime) {
    endTimeError = "Completá el horario de este día.";
  } else if (!SCHEDULE_TIME_PATTERN.test(endTime)) {
    endTimeError = "Ingresá un horario válido.";
  }

  if (!startTimeError && !endTimeError && endTime <= startTime) {
    endTimeError = "La hora “Hasta” debe ser posterior a “Desde”.";
  }

  return {
    valid: !startTimeError && !endTimeError,
    startTimeError,
    endTimeError,
  };
}

function rangeForAgenda() {
  const from = new Date();
  from.setHours(0, 0, 0, 0);
  const to = new Date(from);
  to.setDate(to.getDate() + 60);
  to.setHours(23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
}

export default function ProjectAgendaPage() {
  const mobile = useMediaQuery("(max-width:599.95px)", { noSsr: true });
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedServiceId = searchParams.get("servicio") || "";
  const newServiceFlow = searchParams.get("nuevo") === "1";
  const requestedAction = String(searchParams.get("accion") || "").trim().toLowerCase();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [services, setServices] = React.useState([]);
  const [selectedServiceId, setSelectedServiceId] = React.useState("");
  const [appointments, setAppointments] = React.useState([]);
  const [blocks, setBlocks] = React.useState([]);
  const [schedule, setSchedule] = React.useState(defaultSchedule);
  const [scheduleBaseline, setScheduleBaseline] = React.useState(null);
  const [pendingServiceId, setPendingServiceId] = React.useState("");
  const [serviceChangeDialogOpen, setServiceChangeDialogOpen] = React.useState(false);
  const [servicesLoading, setServicesLoading] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [savingSchedule, setSavingSchedule] = React.useState(false);
  const [savingAppointment, setSavingAppointment] = React.useState(false);
  const [savingBlock, setSavingBlock] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [appointmentDialog, setAppointmentDialog] = React.useState(false);
  const [editingAppointmentId, setEditingAppointmentId] = React.useState("");
  const [editingAppointmentStartAt, setEditingAppointmentStartAt] = React.useState("");
  const [blockDialog, setBlockDialog] = React.useState(false);
  const [appointmentForm, setAppointmentForm] = React.useState(emptyAppointment);
  const [blockForm, setBlockForm] = React.useState(emptyBlock);

  const project = React.useMemo(
    () =>
      projects.find(
        (item) => item.slug === projectId || item.id === projectId,
      ) || activeProject,
    [projects, projectId, activeProject],
  );
  const productsEnabled = Boolean(
    project && isProjectModuleEnabled(project, "products"),
  );
  const selectedService = React.useMemo(
    () => services.find((service) => itemId(service) === selectedServiceId),
    [services, selectedServiceId],
  );
  const hasUnsavedScheduleChanges = React.useMemo(
    () =>
      Boolean(
        selectedServiceId &&
          scheduleBaseline &&
          !schedulesMatch(schedule, scheduleBaseline),
      ),
    [schedule, scheduleBaseline, selectedServiceId],
  );
  const scheduleValidation = React.useMemo(
    () =>
      Object.fromEntries(
        schedule.map((day) => [day.weekday, validateScheduleDay(day)]),
      ),
    [schedule],
  );
  const hasInvalidSchedule = React.useMemo(
    () => Object.values(scheduleValidation).some((result) => !result.valid),
    [scheduleValidation],
  );
  const projectTimezone = project?.timezone || TIMEZONE;
  const quickActionHandledRef = React.useRef("");
  const openAppointmentDialog = React.useCallback((appointment = null) => {
    if (!selectedService) return;
    if (appointment) {
      setEditingAppointmentId(appointment.id || appointment._id || "");
      setEditingAppointmentStartAt(appointment.startAt || "");
      setAppointmentForm({
        fullName: appointment.customer?.fullName || "",
        phone: appointment.customer?.phone || "",
        email: appointment.customer?.email || "",
        startAt: isoToLocalInput(appointment.startAt),
        notes: appointment.notes || "",
        status: appointment.status || "pending",
        paymentStatus: appointment.payment?.status || "not_required",
      });
      setAppointmentDialog(true);
      return;
    }
    const mode = selectedService?.bookingPaymentMode || selectedService?.pagoReserva || "none";
    setEditingAppointmentId("");
    setEditingAppointmentStartAt("");
    setAppointmentForm({
      ...emptyAppointment,
      paymentStatus: mode === "none" ? "not_required" : "pending",
    });
    setAppointmentDialog(true);
  }, [selectedService]);

  const closeAppointmentDialog = React.useCallback(() => {
    if (savingAppointment) return;
    setAppointmentDialog(false);
    setEditingAppointmentId("");
    setEditingAppointmentStartAt("");
    setAppointmentForm(emptyAppointment);
  }, [savingAppointment]);

  React.useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  React.useEffect(() => {
    setServices([]);
    setServicesLoading(true);
    setSelectedServiceId("");
    setAppointments([]);
    setBlocks([]);
    setSchedule(defaultSchedule());
    setScheduleBaseline(null);
    setPendingServiceId("");
    setServiceChangeDialogOpen(false);
    setError("");
    setSuccess("");
  }, [projectId]);

  React.useEffect(() => {
    if (projectId && (!activeProject || activeProject.slug !== projectId)) {
      const found = projects.find(
        (item) => item.slug === projectId || item.id === projectId,
      );
      if (found) setActiveProject(found);
    }
  }, [projectId, projects, activeProject, setActiveProject]);

  const loadServices = React.useCallback(async () => {
    if (!projectId || !productsEnabled) {
      setServicesLoading(false);
      return;
    }
    setServicesLoading(true);
    try {
      const data = await productsApi.list(projectId, { type: "service" });
      const nextServices = (data.products || []).filter(
        (item) => normalizeType(item) === "service",
      );
      setServices(nextServices);
      setSelectedServiceId((current) => {
        if (requestedServiceId && nextServices.some((item) => itemId(item) === requestedServiceId)) {
          return requestedServiceId;
        }
        if (nextServices.some((item) => itemId(item) === current)) return current;
        const preferred = nextServices.find(requiresBooking) || nextServices[0];
        return preferred ? itemId(preferred) : "";
      });
    } finally {
      setServicesLoading(false);
    }
  }, [projectId, productsEnabled, requestedServiceId]);

  const loadAgenda = React.useCallback(async () => {
    if (!projectId || !selectedServiceId || !productsEnabled) {
      setAppointments([]);
      setBlocks([]);
      setSchedule(defaultSchedule());
      setScheduleBaseline(null);
      return;
    }
    try {
      setLoading(true);
      setError("");
      const range = rangeForAgenda();
      const [appointmentsData, availabilityData, blocksData] = await Promise.all([
        appointmentsApi.list(projectId, {
          ...range,
          serviceId: selectedServiceId,
        }),
        appointmentsApi.getAvailability(projectId, selectedServiceId),
        appointmentsApi.listBlocks(projectId, {
          ...range,
          serviceId: selectedServiceId,
        }),
      ]);
      const loadedSchedule = rulesToSchedule(availabilityData.rules || []);
      setAppointments(appointmentsData.appointments || []);
      setSchedule(loadedSchedule);
      setScheduleBaseline(loadedSchedule);
      setBlocks(blocksData.blocks || []);
    } catch (err) {
      setError(err.message || "No se pudo cargar la agenda");
    } finally {
      setLoading(false);
    }
  }, [projectId, selectedServiceId, productsEnabled]);

  React.useEffect(() => {
    if (!projectId || !productsEnabled) return;
    loadServices().catch((err) => {
      setError(err.message || "No se pudieron cargar los servicios");
    });
  }, [loadServices, projectId, productsEnabled]);

  React.useEffect(() => {
    loadAgenda();
  }, [loadAgenda]);

  const updateScheduleDay = (weekday, patch) => {
    setSchedule((current) =>
      current.map((day) =>
        day.weekday === weekday ? { ...day, ...patch } : day,
      ),
    );
  };

  const selectService = React.useCallback((nextServiceId) => {
    setScheduleBaseline(null);
    setSelectedServiceId(nextServiceId);
  }, []);

  const handleServiceChange = (event) => {
    const nextServiceId = event.target.value;
    if (!nextServiceId || nextServiceId === selectedServiceId) return;
    if (hasUnsavedScheduleChanges) {
      setPendingServiceId(nextServiceId);
      setServiceChangeDialogOpen(true);
      return;
    }
    selectService(nextServiceId);
  };

  const closeServiceChangeDialog = () => {
    if (savingSchedule) return;
    setServiceChangeDialogOpen(false);
    setPendingServiceId("");
  };

  const saveSchedule = async () => {
    if (
      !selectedServiceId ||
      savingSchedule ||
      !hasUnsavedScheduleChanges ||
      hasInvalidSchedule
    ) {
      return false;
    }
    try {
      setSavingSchedule(true);
      setError("");
      setSuccess("");
      const rules = schedule
        .filter((day) => day.enabled)
        .map((day) => ({
          weekday: day.weekday,
          startTime: day.startTime,
          endTime: day.endTime,
          active: true,
        }));
      const response = await appointmentsApi.saveAvailability(projectId, selectedServiceId, {
        timezone: projectTimezone,
        rules,
      });
      const savedSchedule = rulesToSchedule(response?.rules || rules);
      setSchedule(savedSchedule);
      setScheduleBaseline(savedSchedule);
      setSuccess("Disponibilidad semanal guardada.");
      return true;
    } catch (err) {
      setError(err.message || "No se pudo guardar la disponibilidad");
      return false;
    } finally {
      setSavingSchedule(false);
    }
  };

  const discardScheduleAndChangeService = () => {
    if (!pendingServiceId || savingSchedule) return;
    const nextServiceId = pendingServiceId;
    setServiceChangeDialogOpen(false);
    setPendingServiceId("");
    selectService(nextServiceId);
  };

  const saveScheduleAndChangeService = async () => {
    if (!pendingServiceId || savingSchedule) return;
    const nextServiceId = pendingServiceId;
    const saved = await saveSchedule();
    if (!saved) {
      setServiceChangeDialogOpen(false);
      setPendingServiceId("");
      return;
    }
    setServiceChangeDialogOpen(false);
    setPendingServiceId("");
    selectService(nextServiceId);
  };

  React.useEffect(() => {
    if (requestedAction !== "crear-turno" || !selectedService) return;
    const token = `${projectId}:${selectedServiceId}:${searchParams.toString()}`;
    if (quickActionHandledRef.current === token) return;
    quickActionHandledRef.current = token;
    openAppointmentDialog();
    const next = new URLSearchParams(searchParams);
    next.delete("accion");
    setSearchParams(next, { replace: true });
  }, [openAppointmentDialog, projectId, requestedAction, searchParams, selectedService, selectedServiceId, setSearchParams]);

  const saveAppointment = async (event) => {
    event.preventDefault();
    try {
      setSavingAppointment(true);
      setError("");
      setSuccess("");
      if (editingAppointmentId) {
        const payload = {
          notes: appointmentForm.notes,
          status: appointmentForm.status,
          paymentStatus: appointmentForm.paymentStatus,
        };
        if (appointmentForm.startAt !== isoToLocalInput(editingAppointmentStartAt)) {
          payload.startAt = localInputToIso(appointmentForm.startAt);
        }
        await appointmentsApi.update(projectId, editingAppointmentId, payload);
      } else {
        await appointmentsApi.create(projectId, {
          serviceId: selectedServiceId,
          fullName: appointmentForm.fullName,
          phone: appointmentForm.phone,
          email: appointmentForm.email,
          startAt: localInputToIso(appointmentForm.startAt),
          notes: appointmentForm.notes,
          status: appointmentForm.status,
          paymentStatus: appointmentForm.paymentStatus,
          timezone: projectTimezone,
        });
      }
      setAppointmentDialog(false);
      setEditingAppointmentId("");
      setEditingAppointmentStartAt("");
      setAppointmentForm(emptyAppointment);
      setSuccess(editingAppointmentId ? "Turno actualizado." : "Turno cargado en la agenda.");
      await loadAgenda();
    } catch (err) {
      setError(err.message || (editingAppointmentId ? "No se pudo actualizar el turno" : "No se pudo crear el turno"));
    } finally {
      setSavingAppointment(false);
    }
  };

  const createBlock = async (event) => {
    event.preventDefault();
    try {
      setSavingBlock(true);
      setError("");
      setSuccess("");
      await appointmentsApi.createBlock(projectId, {
        serviceId:
          blockForm.scope === "service" ? selectedServiceId : undefined,
        startAt: localInputToIso(blockForm.startAt),
        endAt: localInputToIso(blockForm.endAt),
        reason: blockForm.reason,
      });
      setBlockDialog(false);
      setBlockForm(emptyBlock);
      setSuccess("Horario bloqueado.");
      await loadAgenda();
    } catch (err) {
      setError(err.message || "No se pudo bloquear el horario");
    } finally {
      setSavingBlock(false);
    }
  };

  const removeBlock = async (block) => {
    const ok = window.confirm("¿Quitar este bloqueo de la agenda?");
    if (!ok) return;
    try {
      await appointmentsApi.removeBlock(projectId, block.id || block._id);
      await loadAgenda();
    } catch (err) {
      setError(err.message || "No se pudo quitar el bloqueo");
    }
  };

  const summary = React.useMemo(() => {
    const now = new Date();
    return {
      upcoming: appointments.filter(
        (item) =>
          ACTIVE_STATUSES.includes(item.status) && new Date(item.startAt) >= now,
      ).length,
      pending: appointments.filter((item) => item.status === "pending").length,
      paid: appointments.filter((item) => item.payment?.status === "approved")
        .length,
      blocks: blocks.length,
    };
  }, [appointments, blocks]);

  if (project && !productsEnabled) {
    return (
      <ModuleDisabledState
        projectId={projectId}
        title="Agenda de servicios"
        description="La agenda depende del módulo de productos y servicios del proyecto."
      />
    );
  }

  if (servicesLoading) {
    return <DashboardLoadingState label="Cargando agenda de servicios…" minHeight={360} />;
  }

  return (
    <Stack spacing={3} sx={{ pb: 5 }}>
      <Stack
        direction={{ xs: "column", lg: "row" }}
        justifyContent="space-between"
        alignItems={{ lg: "center" }}
        spacing={2}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 950, color: BRAND.dark, letterSpacing: -0.8 }}
          >
            Agenda de servicios
          </Typography>
          <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)", mt: 0.5, display: { xs: "none", sm: "block" } }}>
            Disponibilidad, bloqueos y turnos cargados manualmente.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ width: { xs: "100%", lg: "auto" } }}>
          <Tooltip title="Actualizar agenda">
            <span>
              <IconButton
                onClick={loadAgenda}
                disabled={loading || !selectedServiceId}
                sx={{ border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.12)", borderRadius: 2.5 }}
              >
                <RefreshRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<BlockRoundedIcon />}
            disabled={!selectedServiceId}
            onClick={() => setBlockDialog(true)}
            sx={{ textTransform: "none", fontWeight: 850, borderRadius: 2.5, minHeight: 44, flex: { xs: "1 1 150px", sm: "0 0 auto" } }}
          >
            Bloquear
          </Button>
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            disabled={!selectedServiceId}
            onClick={() => openAppointmentDialog()}
            sx={{
              textTransform: "none",
              fontWeight: 900,
              borderRadius: 2.5,
              bgcolor: BRAND.orange,
              "&:hover": { bgcolor: "#DB4B2F" },
              minHeight: 44,
              flex: { xs: "1 1 150px", sm: "0 0 auto" },
            }}
          >
            Cargar turno
          </Button>
        </Stack>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {success ? <Alert severity="success">{success}</Alert> : null}

      {newServiceFlow && selectedService ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: 3,
            border: "1px solid rgba(243,90,58,.24)",
            bgcolor: "rgba(243,90,58,.05)",
          }}
        >
          <Typography variant="caption" sx={{ color: BRAND.orange, fontWeight: 950, letterSpacing: .6 }}>
            SERVICIO CREADO · FALTA UN PASO
          </Typography>
          <Typography sx={{ color: BRAND.dark, fontWeight: 950, mt: .25 }}>
            Definí los días y guardá los horarios
          </Typography>
          <Stack direction="row" spacing={.7} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            <Chip size="small" color="success" label="1 Servicio listo" />
            <Chip size="small" color="primary" label="2 Guardar horarios" />
            <Chip size="small" variant="outlined" label="3 Cliente reserva en la web" />
          </Stack>
        </Paper>
      ) : null}

      <Paper
        elevation={0}
        sx={{ p: 2.5, borderRadius: 3, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
          <TextField
            select
            label="Servicio"
            value={selectedServiceId}
            onChange={handleServiceChange}
            disabled={savingSchedule}
            fullWidth
          >
            {services.map((service) => (
              <MenuItem key={itemId(service)} value={itemId(service)}>
                {itemName(service)} · {durationLabel(service)}
                {!requiresBooking(service) ? " · Turnos desactivados" : ""}
              </MenuItem>
            ))}
          </TextField>
          <ContextualHelp
            compact
            title="Cómo funciona esta agenda"
            text="Los horarios se configuran por servicio. Los bloqueos pueden afectar sólo al servicio elegido o a toda la agenda."
            bullets={[
              "Los turnos activos respetan capacidad, duración y bloqueos reales.",
              "Al mover un turno, el backend vuelve a validar conflictos antes de guardar.",
            ]}
          />
        </Stack>
        {!services.length ? (
          <Alert
            severity="info"
            sx={{ mt: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate(`${dashboardProjectItemsPath(project, { projectBase: `/dashboard/proyectos/${projectId}` })}?crear=1&tipo=service`)}
              >
                Crear servicio
              </Button>
            }
          >
            Primero creá un servicio con reserva online.
          </Alert>
        ) : selectedService && !requiresBooking(selectedService) ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Este servicio todavía no tiene habilitada la reserva. Podés preparar su agenda, pero no debería publicarse hasta activar esa opción en Productos y servicios.
          </Alert>
        ) : null}
      </Paper>

      {loading ? <LinearProgress /> : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2,1fr)", lg: "repeat(4,1fr)" },
          gap: 2,
        }}
      >
        {[
          ["Próximos turnos", summary.upcoming],
          ["Por confirmar", summary.pending],
          ["Pagados", summary.paid],
          ["Bloqueos", summary.blocks],
        ].map(([label, value]) => (
          <Paper
            key={label}
            elevation={0}
            sx={{ p: 2.25, borderRadius: 3, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}
          >
            <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 800 }}>
              {label}
            </Typography>
            <Typography variant="h4" sx={{ mt: 0.4, color: BRAND.dark, fontWeight: 950 }}>
              {value}
            </Typography>
          </Paper>
        ))}
      </Box>

      <Paper
        elevation={0}
        sx={{ borderRadius: 3, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)", overflow: "hidden" }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          spacing={1}
          sx={{ p: { xs: 1.7, sm: 2.5 } }}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <Typography variant="h6" sx={{ color: BRAND.dark, fontWeight: 950 }}>
                Disponibilidad semanal
              </Typography>
              {hasUnsavedScheduleChanges ? (
                <Chip size="small" color="warning" label="Cambios sin guardar" />
              ) : null}
            </Stack>
            <Stack direction="row" alignItems="center" spacing={.5} sx={{ mt: .25 }}>
              <Typography variant="body2" color="text.secondary">Horario habitual del servicio.</Typography>
              <ContextualHelp
                compact
                label=""
                title="Disponibilidad y excepciones"
                text="Esta vista guarda una franja por día. Usá Bloquear para feriados, ausencias o cierres puntuales sin cambiar la semana habitual."
              />
            </Stack>
          </Box>
          <Button
            startIcon={<SaveRoundedIcon />}
            variant="contained"
            disabled={
              !selectedServiceId ||
              savingSchedule ||
              !hasUnsavedScheduleChanges ||
              hasInvalidSchedule
            }
            onClick={saveSchedule}
            sx={{ textTransform: "none", fontWeight: 900, borderRadius: 2.5, minHeight: 44, width: { xs: "100%", sm: "auto" } }}
          >
            {savingSchedule ? "Guardando…" : "Guardar horarios"}
          </Button>
        </Stack>
        <Divider />
        <Stack spacing={0}>
          {schedule.map((day) => (
            <Stack
              key={day.weekday}
              direction={{ xs: "column", md: "row" }}
              alignItems={{ sm: "center" }}
              spacing={{ xs: 1, md: 2 }}
              sx={{ px: { xs: 1.5, sm: 2.5 }, py: 1.35, borderBottom: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.07)" }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: { md: 170 }, width: { xs: "100%", md: "auto" } }}>
                <Switch
                  checked={day.enabled}
                  onChange={(event) =>
                    updateScheduleDay(day.weekday, { enabled: event.target.checked })
                  }
                />
                <Typography sx={{ color: BRAND.dark, fontWeight: 850 }}>{day.label}</Typography>
              </Stack>
              <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 1, width: { xs: "100%", md: "auto" } }}>
                <TextField
                  label="Desde"
                  type="time"
                  value={day.startTime}
                  disabled={!day.enabled}
                  onChange={(event) =>
                    updateScheduleDay(day.weekday, { startTime: event.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(scheduleValidation[day.weekday]?.startTimeError)}
                  helperText={scheduleValidation[day.weekday]?.startTimeError || ""}
                  size="small"
                  sx={{ minWidth: 0 }}
                />
                <TextField
                  label="Hasta"
                  type="time"
                  value={day.endTime}
                  disabled={!day.enabled}
                  onChange={(event) =>
                    updateScheduleDay(day.weekday, { endTime: event.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(scheduleValidation[day.weekday]?.endTimeError)}
                  helperText={scheduleValidation[day.weekday]?.endTimeError || ""}
                  size="small"
                  sx={{ minWidth: 0 }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>
                {day.enabled ? "Disponible" : "No se ofrecen turnos"}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{ borderRadius: 3, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)", overflow: "hidden" }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ p: { xs: 1.7, sm: 2.5 } }}>
          <EventAvailableRoundedIcon sx={{ color: BRAND.orange }} />
          <Box>
            <Typography variant="h6" sx={{ color: BRAND.dark, fontWeight: 950 }}>
              Próximos turnos
            </Typography>
            <Typography variant="body2" color="text.secondary">Próximos 60 días.</Typography>
          </Box>
        </Stack>
        <Divider />
        <TableContainer>
          <ResponsiveOperationalTable mobileLabel="Próximos turnos" collapseAfter={4} sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                {["Fecha", "Cliente", "Estado", "Pago", "Acción"].map((label) => (
                  <TableCell key={label} sx={{ fontWeight: 950, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!appointments.length ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 7, textAlign: "center" }}>
                    <CalendarMonthRoundedIcon sx={{ fontSize: 38, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }} />
                    <Typography sx={{ mt: 1, color: BRAND.dark, fontWeight: 900 }}>
                      No hay turnos en esta ventana.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {appointments.map((appointment) => (
                <TableRow
                  key={appointment.id || appointment._id}
                  hover
                  onClick={() => openAppointmentDialog(appointment)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Typography sx={{ color: BRAND.dark, fontWeight: 850 }}>
                      {formatDateTime(appointment.startAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {appointment.appointmentNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: BRAND.dark, fontWeight: 800 }}>
                      {appointment.customer?.fullName || "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {appointment.customer?.phone || ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={statusLabel(appointment.status || "pending")} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={paymentLabel(appointment.payment?.status || "not_required")} />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<EditRoundedIcon />}
                      onClick={(event) => {
                        event.stopPropagation();
                        openAppointmentDialog(appointment);
                      }}
                      sx={{ minHeight: 40, textTransform: "none", fontWeight: 850 }}
                    >
                      Abrir
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ResponsiveOperationalTable>
        </TableContainer>
      </Paper>

      <Paper
        elevation={0}
        sx={{ borderRadius: 3, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)", overflow: "hidden" }}
      >
        <Box sx={{ p: 2.5 }}>
          <Typography variant="h6" sx={{ color: BRAND.dark, fontWeight: 950 }}>
            Bloqueos vigentes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Horarios ocupados manualmente, ausencias o cierres excepcionales.
          </Typography>
        </Box>
        <Divider />
        <TableContainer>
          <ResponsiveOperationalTable mobileLabel="Bloqueos de agenda" collapseAfter={3} sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 950 }}>Desde</TableCell>
                <TableCell sx={{ fontWeight: 950 }}>Hasta</TableCell>
                <TableCell sx={{ fontWeight: 950 }}>Alcance</TableCell>
                <TableCell sx={{ fontWeight: 950 }}>Motivo</TableCell>
                <TableCell align="right" sx={{ fontWeight: 950 }}>Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!blocks.length ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                    No hay bloqueos vigentes.
                  </TableCell>
                </TableRow>
              ) : null}
              {blocks.map((block) => (
                <TableRow key={block.id || block._id}>
                  <TableCell>{formatDateTime(block.startAt)}</TableCell>
                  <TableCell>{formatDateTime(block.endAt)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={block.serviceId ? "Este servicio" : "Toda la agenda"}
                    />
                  </TableCell>
                  <TableCell>{block.reason || "Sin motivo"}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Quitar bloqueo">
                      <IconButton onClick={() => removeBlock(block)} sx={{ color: BRAND.orange }}>
                        <DeleteRoundedIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ResponsiveOperationalTable>
        </TableContainer>
      </Paper>

      <Dialog
        open={serviceChangeDialogOpen}
        onClose={closeServiceChangeDialog}
        disableEscapeKeyDown={savingSchedule}
        fullWidth
        maxWidth="xs"
        aria-labelledby="unsaved-schedule-dialog-title"
      >
        <DialogTitle id="unsaved-schedule-dialog-title" sx={{ fontWeight: 950, color: BRAND.dark }}>
          Tenés cambios sin guardar
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Cambiaste los horarios de este servicio. ¿Qué querés hacer antes de abrir el siguiente?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            gap: 1,
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            "& > :not(style)": { m: 0, minHeight: 44 },
          }}
        >
          <Button
            autoFocus
            onClick={closeServiceChangeDialog}
            disabled={savingSchedule}
            variant="text"
            sx={{ textTransform: "none", fontWeight: 850 }}
          >
            Seguir editando
          </Button>
          <Button
            onClick={discardScheduleAndChangeService}
            disabled={savingSchedule}
            color="error"
            variant="outlined"
            sx={{ textTransform: "none", fontWeight: 850 }}
          >
            Descartar cambios
          </Button>
          <Button
            onClick={saveScheduleAndChangeService}
            disabled={savingSchedule}
            variant="contained"
            startIcon={<SaveRoundedIcon />}
            sx={{ textTransform: "none", fontWeight: 900 }}
          >
            {savingSchedule ? "Guardando…" : "Guardar y cambiar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={appointmentDialog}
        onClose={closeAppointmentDialog}
        fullWidth
        maxWidth="sm"
        fullScreen={mobile}
        PaperProps={{ sx: { borderRadius: mobile ? 0 : 3.5, maxHeight: mobile ? "100dvh" : "calc(100dvh - 48px)" } }}
      >
        <Box component="form" onSubmit={saveAppointment} sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: mobile ? "100dvh" : "auto" }}>
          <DialogTitle sx={{ fontWeight: 950, color: BRAND.dark, pr: 6, position: "relative" }}>
            {editingAppointmentId ? "Modificar turno" : "Cargar turno"}
            <IconButton
              onClick={closeAppointmentDialog}
              disabled={savingAppointment}
              aria-label="Cerrar turno"
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: { xs: 1.5, sm: 3 } }}>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Chip
                  color="primary"
                  variant="outlined"
                  label={selectedService ? `${itemName(selectedService)} · ${durationLabel(selectedService)}` : "Servicio"}
                />
                <ContextualHelp
                  compact
                  title="Conflictos y horarios"
                  text="Al guardar, la agenda vuelve a validar bloqueos, duración y capacidad. Si el horario dejó de estar libre, el turno no se modifica."
                />
              </Stack>
              {editingAppointmentId ? (
                <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 2.2 }}>
                  <Typography sx={{ color: BRAND.dark, fontWeight: 900 }}>{appointmentForm.fullName || "Cliente"}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: .2 }}>
                    {[appointmentForm.phone, appointmentForm.email].filter(Boolean).join(" · ") || "Sin datos de contacto"}
                  </Typography>
                </Paper>
              ) : (
                <>
                  <TextField
                    label="Nombre del cliente"
                    required
                    value={appointmentForm.fullName}
                    onChange={(event) => setAppointmentForm((current) => ({ ...current, fullName: event.target.value }))}
                    fullWidth
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label="Teléfono"
                      required
                      value={appointmentForm.phone}
                      onChange={(event) => setAppointmentForm((current) => ({ ...current, phone: event.target.value }))}
                      fullWidth
                      inputProps={{ inputMode: "tel" }}
                    />
                    <TextField
                      label="Email"
                      type="email"
                      value={appointmentForm.email}
                      onChange={(event) => setAppointmentForm((current) => ({ ...current, email: event.target.value }))}
                      fullWidth
                    />
                  </Stack>
                </>
              )}
              <TextField
                label="Fecha y hora"
                type="datetime-local"
                required
                value={appointmentForm.startAt}
                onChange={(event) => setAppointmentForm((current) => ({ ...current, startAt: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  select
                  label="Estado"
                  value={appointmentForm.status}
                  onChange={(event) => setAppointmentForm((current) => ({ ...current, status: event.target.value }))}
                  fullWidth
                >
                  {(editingAppointmentId
                    ? ["pending", "confirmed", "completed", "no_show", "cancelled"]
                    : ["pending", "confirmed"]
                  ).map((status) => (
                    <MenuItem key={status} value={status}>{statusLabel(status)}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Estado del pago"
                  value={appointmentForm.paymentStatus}
                  onChange={(event) => setAppointmentForm((current) => ({ ...current, paymentStatus: event.target.value }))}
                  fullWidth
                >
                  {(editingAppointmentId
                    ? ["not_required", "pending", "in_process", "approved", "rejected", "cancelled", "refunded"]
                    : ["not_required", "pending", "approved"]
                  ).map((status) => (
                    <MenuItem key={status} value={status}>{paymentLabel(status)}</MenuItem>
                  ))}
                </TextField>
              </Stack>
              <TextField
                label="Notas internas"
                multiline
                minRows={2}
                value={appointmentForm.notes}
                onChange={(event) => setAppointmentForm((current) => ({ ...current, notes: event.target.value }))}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, gap: 1, flexDirection: { xs: "column-reverse", sm: "row" }, alignItems: "stretch" }}>
            <Button onClick={closeAppointmentDialog} disabled={savingAppointment} sx={{ minHeight: 44 }}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={savingAppointment} sx={{ minHeight: 44 }}>
              {savingAppointment ? "Guardando…" : editingAppointmentId ? "Guardar cambios" : "Guardar turno"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={blockDialog}
        onClose={() => !savingBlock && setBlockDialog(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={mobile}
        PaperProps={{ sx: { borderRadius: mobile ? 0 : 3.5 } }}
      >
        <Box component="form" onSubmit={createBlock} sx={{ display: "flex", flexDirection: "column", height: mobile ? "100dvh" : "auto" }}>
          <DialogTitle sx={{ fontWeight: 950, color: BRAND.dark, pr: 6, position: "relative" }}>
            Bloquear horario
            <IconButton
              onClick={() => setBlockDialog(false)}
              disabled={savingBlock}
              aria-label="Cerrar bloqueo"
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: { xs: 1.5, sm: 3 } }}>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <TextField
                select
                label="Aplicar a"
                value={blockForm.scope}
                onChange={(event) => setBlockForm((current) => ({ ...current, scope: event.target.value }))}
                fullWidth
              >
                <MenuItem value="service">Solo {itemName(selectedService)}</MenuItem>
                <MenuItem value="all">Toda la agenda</MenuItem>
              </TextField>
              <TextField
                label="Desde"
                type="datetime-local"
                required
                value={blockForm.startAt}
                onChange={(event) => setBlockForm((current) => ({ ...current, startAt: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Hasta"
                type="datetime-local"
                required
                value={blockForm.endAt}
                onChange={(event) => setBlockForm((current) => ({ ...current, endAt: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Motivo"
                value={blockForm.reason}
                onChange={(event) => setBlockForm((current) => ({ ...current, reason: event.target.value }))}
                placeholder="Turno telefónico, feriado, ausencia, capacitación..."
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, gap: 1, flexDirection: { xs: "column-reverse", sm: "row" }, alignItems: "stretch" }}>
            <Button onClick={() => setBlockDialog(false)} disabled={savingBlock} sx={{ minHeight: 44 }}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={savingBlock} sx={{ minHeight: 44 }}>
              {savingBlock ? "Guardando…" : "Bloquear"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Stack>
  );
}
